import React, { useState } from 'react';
import { devLog } from '../../../../utils/devLog';
import {
  Calendar, User, CheckSquare, AlertCircle, Plus, MapPin, FileText
} from 'lucide-react';
import { ToggleSwitch } from '../../shared/FormComponents';
import EquipmentUnitSelector from './EquipmentUnitSelector';
import { displayNameWithRole, displayName } from '../../modules/crew/viewHelpers';
import Dropdown from '../../../common/Dropdown';

const MaintenanceCheckForm = ({
  formData,
  setFormData,
  relevantEquipment,
  onShowAddEquipment,
  job,
  crewList = []
}) => {
  // Check if we should show detailed item tracking
  // In full-screen view, only show tracking for equipment actually linked to this job
  // In modal view, show tracking for filtered relevant equipment
  const jobLinkedEquipment = job?.linkedEquipmentIds ? 
    relevantEquipment.filter(item => job.linkedEquipmentIds.includes(item.id)) : 
    relevantEquipment;

  // Check if we have real components worth showing detailed tracking for
  const hasRealComponents = jobLinkedEquipment && 
    jobLinkedEquipment.length > 0 && 
    !jobLinkedEquipment.every(item => 
      item.name === "general" || 
      item.location === "general" ||
      !item.name ||
      item.name.toLowerCase().includes('general')
    );

  // Hide component tracking if:
  // 1. No real components OR
  // 2. Job has vessel-level linkage (no per-item tracking needed) OR  
  // 3. Job has no linkedEquipmentIds (vessel-level job)
  const isVesselLevelJob = !job?.linkedEquipmentIds || job?.linkedEquipmentIds?.length === 0;
  const showComponentTracking = hasRealComponents && !isVesselLevelJob;

  // Debug logging to understand what's in relevantEquipment
  React.useEffect(() => {








    if (jobLinkedEquipment && jobLinkedEquipment.length > 0) {

      jobLinkedEquipment.forEach((item, index) => {

      });
    } else {

    }

  }, [relevantEquipment, job, jobLinkedEquipment, hasRealComponents, isVesselLevelJob, showComponentTracking]);

  // Initialize from formData if pre-selected, otherwise empty array
  const [selectedEquipmentUnits, setSelectedEquipmentUnits] = useState(
    formData.selectedEquipmentUnits || []
  );

  // Update formData when equipment units are selected
  const handleEquipmentSelection = (unitIds) => {
    setSelectedEquipmentUnits(unitIds);
    setFormData(prev => ({
      ...prev,
      selectedEquipmentUnits: unitIds
    }));
  };
  
  // Debug: Log crew data structure
  React.useEffect(() => {
    if (crewList.length > 0) {
      devLog('[MaintenanceCheckForm] Crew list received:', crewList);
      devLog('[MaintenanceCheckForm] First crew member structure:', crewList[0]);
    }
  }, [crewList]);
  
  return (
    <div className="space-y-6">
      {/* Two Column Layout for Desktop - Single column when no components */}
      <div className={`grid grid-cols-1 ${showComponentTracking ? 'lg:grid-cols-2' : ''} gap-6`}>
        {/* Left Column - Check Details */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider pl-3 -ml-3">Check Details</h3>
          
          {/* Date and Inspector */}
          <div className="space-y-4">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                <Calendar className="inline h-4 w-4 mr-1 text-cyan-600" />
                Date Completed
              </label>
              <input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
              />
            </div>
            
            <div>
              <label htmlFor="inspector" className="block text-sm font-medium text-gray-700 mb-1">
                <User className="inline h-4 w-4 mr-1 text-cyan-600" />
                Completed By
              </label>
              <Dropdown
                value={formData.inspector}
                onChange={(value) => {
                  const selected = crewList.find(c => c.id === value);
                  setFormData({
                    ...formData,
                    inspector: value,
                    inspectorName: selected ? displayName(selected) : null
                  });
                }}
                options={[
                  { value: '', label: 'Select crew member' },
                  ...(crewList && crewList.length > 0
                    ? crewList.map(crew => ({
                        value: crew.id,
                        label: displayNameWithRole(crew)
                      }))
                    : [{ value: '', label: 'No crew members available - add crew first', disabled: true }]
                  )
                ]}
                width="w-full"
              />
            </div>
          </div>

          {/* Completion Confirmation */}
          <div className="border rounded-lg p-4 bg-green-50">
            <div className="flex items-start">
              <input
                id="allUnits"
                type="checkbox"
                checked={formData.allUnitsChecked}
                onChange={(e) => setFormData({ ...formData, allUnitsChecked: e.target.checked })}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded mt-0.5"
              />
              <div className="ml-3">
                <label htmlFor="allUnits" className="font-medium text-gray-900">
                  <CheckSquare className="inline h-4 w-4 mr-1" />
                  All checks completed
                </label>
                <p className="text-sm text-gray-600 mt-1">
                  Confirm that all required checks have been completed
                </p>
              </div>
            </div>
          </div>

          {/* Equipment Selection - Only show selector if nothing pre-selected */}
          {(!formData.selectedEquipmentUnits || formData.selectedEquipmentUnits.length === 0) && (
            <EquipmentUnitSelector
              job={job}
              selectedUnits={selectedEquipmentUnits}
              onSelectionChange={handleEquipmentSelection}
            />
          )}
        </div>

        {/* Right Column - Item Tracking - Hide when no real components */}
        <div className="space-y-4" style={{ display: showComponentTracking ? 'block' : 'none' }}>
          <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider pl-3 -ml-3">Item Tracking</h3>
          
          {/* Professional Mode Toggle */}
          <div className="border rounded-lg p-4 bg-blue-50">
            <ToggleSwitch
              label="Detailed Item Tracking"
              description={jobLinkedEquipment.length > 0 
                ? `Track ${jobLinkedEquipment.length} individual items in this check`
                : job?.category === 'inspection' || job?.category === 'survey' 
                  ? 'Track individual inspection points or areas'
                  : 'Track individual equipment or check items'}
              checked={formData.detailedMode}
              onChange={(checked) => setFormData({ ...formData, detailedMode: checked })}
            />
          </div>

          {/* Simple Mode - Equipment List */}
          {!formData.detailedMode && jobLinkedEquipment.length > 0 && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Items to check:</h5>
              <div className="text-sm text-gray-600 space-y-1">
                {jobLinkedEquipment.map(item => (
                  <div key={item.id} className="flex items-center">
                    {item.isCheckItem ? (
                      <>
                        <CheckSquare className="h-3 w-3 mr-1 text-gray-400" />
                        <span>{item.specifications || item.location}</span>
                      </>
                    ) : (
                      <>
                        <MapPin className="h-3 w-3 mr-1 text-gray-400" />
                        <span>{item.location}</span>
                        {item.specifications && (
                          <span className="ml-2 text-gray-500">({item.specifications})</span>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Add Items Button when no items */}
          {!formData.detailedMode && jobLinkedEquipment.length === 0 && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <p className="text-sm text-gray-600 mb-3">No items recorded for tracking</p>
              <button
                type="button"
                onClick={onShowAddEquipment}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 w-full justify-center"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Items to Track
              </button>
            </div>
          )}

          {/* Detailed Mode - Individual Item Checks */}
          {formData.detailedMode && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium text-gray-700 mb-3">Individual Item Checks</h4>
              
              {jobLinkedEquipment.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500 mb-3">No items recorded</p>
                  <button
                    type="button"
                    onClick={onShowAddEquipment}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-white bg-cyan-600 hover:bg-cyan-700"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Items
                  </button>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {jobLinkedEquipment.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div className="flex items-center flex-1">
                        <input
                          type="checkbox"
                          checked={formData.equipmentChecks[item.id]?.checked || false}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              equipmentChecks: {
                                ...prev.equipmentChecks,
                                [item.id]: {
                                  ...prev.equipmentChecks[item.id],
                                  checked: e.target.checked
                                }
                              }
                            }));
                          }}
                          className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-gray-900">
                            {item.isCheckItem ? (
                              <>
                                <CheckSquare className="inline h-3 w-3 mr-1 text-gray-400" />
                                {item.specifications}
                              </>
                            ) : (
                              <>
                                <MapPin className="inline h-3 w-3 mr-1 text-gray-400" />
                                {item.location}
                                {item.serialNumber && (
                                  <span className="ml-2 text-xs text-gray-500">
                                    (S/N: {item.serialNumber})
                                  </span>
                                )}
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                      <select
                        value={formData.equipmentChecks[item.id]?.status || 'ok'}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            equipmentChecks: {
                              ...prev.equipmentChecks,
                              [item.id]: {
                                ...prev.equipmentChecks[item.id],
                                status: e.target.value
                              }
                            }
                          }));
                        }}
                        className="ml-2 text-sm px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                      >
                        <option value="ok">OK</option>
                        <option value="defect">Defect</option>
                        <option value="na">N/A</option>
                      </select>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={onShowAddEquipment}
                    className="w-full mt-2 inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add More Items
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* General Notes - Full Width */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
          <FileText className="inline h-4 w-4 mr-1" />
          General Notes (Optional)
        </label>
        <textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
          placeholder="Any additional observations or notes..."
        />
      </div>
    </div>
  );
};

export default MaintenanceCheckForm;